import {
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
} from "@mui/material";
import _ from "lodash";
import { FC, FocusEvent, useId, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  getRegisterAttributes,
  ICustomField,
  ValidationHelpers,
} from "@ryde/shared";

import { FullWidthTextFieldWrapper } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";
import { useInfluencedInput } from "./hooks/use-influenced-input";

interface ITextboxTemplateProps extends ICustomField {
  onBlur?: () => void;
  placeholder?: string;
}

export const TextboxTemplate: FC<ITextboxTemplateProps> = ({
  id,
  name,
  placeholder,
  formLabel,
  formAttributes,
  formValidation,
  size,
  labelTranslated,
  e2eData = "textbox-template",
  onBlur,
  uniqueAttrId,
}) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const generatedId = useId();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const { disabled } = getRegisterAttributes(formAttributes);
  // A unit belongs beside the number, not in the label: "Cost" reading 0.0689 is ambiguous,
  // and putting the symbol in the value would make the field a string the form cannot validate.
  const startAdornment = useMemo(
    () =>
      formAttributes?.startAdornment ? (
        <InputAdornment position="start">
          {formAttributes.startAdornment}
        </InputAdornment>
      ) : undefined,
    [formAttributes?.startAdornment],
  );

  useInfluencedInput({ formAttributes, name });

  const onBlurHandler = useEvent(
    (event: FocusEvent<HTMLInputElement>): void => {
      const value = event.target?.value;
      checkCleanupNestedValidation(name, errors);
      const formattedValue = formAttributes?.disableTrimming
        ? value
        : _.trim(value);
      setValue(name as string, formattedValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const onChangeHandler = useEvent(
    (event: FocusEvent<HTMLInputElement>): void => {
      const value = event.target?.value;
      checkCleanupNestedValidation(name, errors);
      setValue(name as string, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  const inputId = uniqueAttrId ?? `${name}-${generatedId}`;

  return name ? (
    <FullWidthTextFieldWrapper>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, ...fieldRest }, fieldState: { error } }) => {
          return (
            <FormControl
              required={requiredComputed}
              data-e2e-field-name={name}
              data-e2e-template="textbox-template"
              error={!!error}
            >
              <TextField
                {...fieldRest}
                data-e2e={e2eData}
                data-e2e-id={`${name}_${id || ""}`}
                onBlur={onBlur ?? onBlurHandler}
                onChange={onChangeHandler}
                label={formLabelTranslated}
                value={value || ""}
                required={requiredComputed}
                error={!!error}
                size={size}
                fullWidth
                disabled={disabled}
                placeholder={placeholder}
                id={inputId}
                {...(startAdornment && {
                  slotProps: { input: { startAdornment } },
                })}
              />
              {error && (
                <FormHelperText>{error.message as string}</FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
      <FieldExplanationIcon formAttributes={formAttributes} />
    </FullWidthTextFieldWrapper>
  ) : null;
};
